import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import type { UpdatePlatformSettingsDto, UpdateUserSettingsDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';

/**
 * Saves a partial UpdateUserSettingsDto (now just personal AI voice) and refreshes the
 * principal. Used by the Personal Voice card on My Profile. Site-wide integrations no
 * longer flow through here — see {@link usePlatformSettingsSave}.
 *
 * `save` resolves to whether the save worked (true) or failed (false, with `error` set); it
 * never rejects.
 */
export function useSettingsSave(onSaved: () => Promise<unknown> | void) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: UpdateUserSettingsDto): Promise<boolean> => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.updateMeSettings(patch);
      await onSaved();
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save your settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saving, saved, setSaved, error, save };
}

/**
 * Saves a partial UpdatePlatformSettingsDto (Tenant-Admin only) and invalidates the
 * masked `['site-integrations']` query so the cards re-read the new set/not-set state.
 * Shared by the Webhook / Apify / Figma / Canva cards on Site Integrations.
 *
 * `save` resolves to whether the save worked (true) or failed (false, with `error` set); it
 * never rejects. The sticky Save footer (useFooterSave) tells success from failure by it, and
 * shows the "Changes saved" toast only on true.
 */
export function usePlatformSettingsSave() {
  const queryClient = useQueryClient();
  const { updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: UpdatePlatformSettingsDto): Promise<boolean> => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.updatePlatformSettings(patch);
      await queryClient.invalidateQueries({ queryKey: ['site-integrations'] });
      // Refresh the "Recent site changes" log so the just-recorded edit shows up.
      await queryClient.invalidateQueries({ queryKey: ['site-audit'] });
      // Re-verify the Apify account ONLY when the Apify key/label actually changed — a Webhook or
      // Figma save shouldn't trigger an outbound Apify /users/me lookup.
      if ('apifyApiKey' in patch || 'apifyUserId' in patch) {
        await queryClient.invalidateQueries({ queryKey: ['apify-identity'] });
      }
      if ('apifyApiKey' in patch) {
        // The Add source dialog's warning reads apifyConfigured off the signed-in user. Ask the
        // server (it also knows about the global fallback token) and MERGE it in: refresh() would
        // remount the whole app.
        try {
          updateUser((await api.me()).user);
        } catch {
          // The save succeeded; the flag corrects on the next load.
        }
      }
      // Same rule for Figma: only a PAT change should cost an outbound Figma /v1/me lookup.
      if ('figmaPat' in patch) {
        await queryClient.invalidateQueries({ queryKey: ['figma-identity'] });
      }
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save these settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saving, saved, setSaved, error, save };
}

/**
 * Standard Save button + inline "Saved" / error row used by the settings cards.
 *
 * `inFooter` (from useFooterSave): the page's sticky footer saves this card, so the row leaves its
 * Save button out and keeps the "Saved" and error lines; with neither to show it renders nothing.
 */
export function SaveRow({
  saving,
  saved,
  dirty,
  error,
  onSave,
  inFooter = false,
}: {
  saving: boolean;
  saved: boolean;
  dirty: boolean;
  error: string | null;
  onSave: () => void;
  inFooter?: boolean;
}) {
  const showSaved = saved && !dirty;
  if (inFooter && !showSaved && !error) return null;
  return (
    <div className="flex items-center gap-3">
      {!inFooter && (
        <Button onClick={onSave} loading={saving} disabled={saving || !dirty}>
          Save
        </Button>
      )}
      {showSaved && (
        <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
          <Check className="h-4 w-4" />
          Saved
        </span>
      )}
      {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
    </div>
  );
}
